import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { 
  HomeIcon, 
  BookOpenIcon, 
  AcademicCapIcon,
  ClockIcon,
  ChartBarIcon,
  CogIcon,
  UserIcon,
  LightBulbIcon,
  PuzzlePieceIcon,
  TrophyIcon,
  CalendarIcon,
  DocumentTextIcon,
  BeakerIcon,
  StarIcon,
  FireIcon,
  GiftIcon,
  UsersIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  Bars3Icon,
  XMarkIcon,
  KeyIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  FolderIcon,
  HeartIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../../store'

import { useTheme } from '../../hooks'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import { ROUTES } from '../../utils/constants'
import { isTablet, isMobile } from '../../utils/helpers'

interface SidebarProps {
  className?: string
}

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string | number
  children?: NavItem[]
}

const navigationItems: NavItem[] = [
  {
    name: 'Ana Sayfa',
    href: ROUTES.DASHBOARD,
    icon: HomeIcon
  },
  {
    name: 'Çalışma',
    href: ROUTES.STUDY.BASE,
    icon: BookOpenIcon,
    children: [
      {
        name: 'Çalışma Planı',
        href: ROUTES.STUDY.PLANS,
        icon: CalendarIcon
      },
      {
        name: 'Soru Bankalarım',
        href: ROUTES.QUESTIONS.BANK,
        icon: DocumentTextIcon
      },
      // Removed: { name: 'Çalışma Oturumları', href: ROUTES.STUDY.SESSIONS, icon: ClockIcon }
      {
        name: 'Süre Teknikleri',
        href: ROUTES.POMODORO,
        icon: ClockIcon
      },
      {
        name: 'Nefes Egzersizleri',
        href: '/tools/breathing',
        icon: HeartIcon
      },
    ]
  },
  {
    name: 'Sınavlar',
    href: ROUTES.EXAMS.BASE,
    icon: AcademicCapIcon,
    children: [
      {
        name: 'Deneme Sınavları',
        href: ROUTES.EXAMS.TRIALS,
        icon: BeakerIcon
      },
      {
        name: 'Sınav Sonuçları',
        href: ROUTES.EXAMS.RESULTS,
        icon: ChartBarIcon
      },
      {
        name: 'Performans Analizi',
        href: ROUTES.ANALYTICS.PERFORMANCE,
        icon: ChartBarIcon
      }
    ]
  },
  {
    name: 'Öğrenme Araçları',
    href: '/tools',
    icon: LightBulbIcon,
    children: [
      {
        name: 'Flashcardlar',
        href: '/flashcards',
        icon: PuzzlePieceIcon
      },
      {
        name: 'Notlar',
        href: ROUTES.NOTES,
        icon: DocumentTextIcon
      },
      {
        name: 'Özet Kartları',
        href: ROUTES.SUMMARIES,
        icon: DocumentTextIcon
      },
      {
        name: 'Zihin Haritaları',
        href: '/tools/mindmaps',
        icon: CircleStackIcon
      },
      // Nefes Egzersizleri öğesini buradan kaldırdık
    ]
  },
  {
    name: 'Gamification',
    href: '/gamification',
    icon: TrophyIcon,
    children: [
      {
        name: 'Rozetler',
        href: ROUTES.GAMIFICATION.BADGES,
        icon: TrophyIcon
      },
      {
        name: 'Başarımlar',
        href: ROUTES.GAMIFICATION.ACHIEVEMENTS,
        icon: StarIcon
      },
      {
        name: 'Liderlik Tablosu',
        href: ROUTES.GAMIFICATION.LEADERBOARD,
        icon: UsersIcon
      },
      {
        name: 'Mağaza',
        href: ROUTES.GAMIFICATION.SHOP,
        icon: GiftIcon
      },
      {
        name: 'Görevler',
        href: ROUTES.GAMIFICATION.QUESTS,
        icon: FireIcon
      }
    ]
  },
  {
    name: 'Analitik',
    href: '/analytics',
    icon: ChartBarIcon,
    children: [
      {
        name: 'Dashboard',
        href: ROUTES.ANALYTICS.DASHBOARD,
        icon: ChartBarIcon
      },
      {
        name: 'İlerleme Raporu',
        href: ROUTES.ANALYTICS.PROGRESS,
        icon: ChartBarIcon
      },
      {
        name: 'Zaman Analizi',
        href: ROUTES.ANALYTICS.TIME,
        icon: ClockIcon
      },
      {
        name: 'Konu Analizi',
        href: ROUTES.ANALYTICS.SUBJECTS,
        icon: BookOpenIcon
      },
      {
        name: 'Performans Analizi',
        href: ROUTES.ANALYTICS.PERFORMANCE,
        icon: ChartBarIcon
      }
    ]
  },
  {
    name: 'Sosyal',
    href: '/social',
    icon: UsersIcon,
    children: [
      {
        name: 'Çalışma Grupları',
        href: ROUTES.SOCIAL.GROUPS,
        icon: UsersIcon
      },
      {
        name: 'Yarışmalar',
        href: ROUTES.SOCIAL.COMPETITIONS,
        icon: TrophyIcon
      },
      {
        name: 'Forum',
        href: ROUTES.SOCIAL.FORUM,
        icon: UsersIcon
      }
    ]
  },
  {
    name: 'Yönetim',
    href: '/admin',
    icon: ShieldCheckIcon,
    children: [
      {
        name: 'Aktivasyon Kodları',
        href: ROUTES.ADMIN.ACTIVATION_CODES,
        icon: KeyIcon
      },
      {
        name: 'Flashcard Yönetimi',
        href: '/admin/flashcards',
        icon: PuzzlePieceIcon
      },
      {
        name: 'Özet Kartı Yönetimi',
        href: '/admin/summary-cards',
        icon: DocumentTextIcon
      }
    ]
  }
]

const bottomNavigationItems: NavItem[] = [
  {
    name: 'Bildirimler',
    href: ROUTES.NOTIFICATIONS,
    icon: BellIcon
  },
  {
    name: 'Yardım',
    href: ROUTES.HELP,
    icon: QuestionMarkCircleIcon
  },
  {
    name: 'Ayarlar',
    href: ROUTES.SETTINGS,
    icon: CogIcon
  },
  {
    name: 'Profil',
    href: ROUTES.PROFILE,
    icon: UserIcon
  }
]

const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const location = useLocation()
  const { sidebarOpen, toggleSidebarOpen } = useTheme()
  const { user } = useSelector((state: RootState) => state.auth)
  const { notifications } = useSelector((state: RootState) => state.ui)
  const { isAdmin } = useAdminAuth()
  
  // Okunmamış bildirim sayısını hesapla
  const unreadCount = notifications.filter(n => !n.read).length
  const [expandedItems, setExpandedItems] = React.useState<string[]>([])

  // Auto-expand current section
  React.useEffect(() => {
    const currentPath = location.pathname
    const expandedSections: string[] = []

    navigationItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => 
          currentPath.startsWith(child.href)
        )
        if (hasActiveChild) {
          expandedSections.push(item.href)
        }
      }
    })

    setExpandedItems(expandedSections)
  }, [location.pathname])

  // Yatay mod değişikliklerini dinle
  React.useEffect(() => {
    const handleOrientationChange = () => {
      // Tablet veya mobil cihazlarda yatay moda geçildiğinde menüyü kapat
      if ((isTablet() || isMobile()) && window.orientation !== undefined) {
        const isLandscape = Math.abs(window.orientation) === 90
        if (isLandscape && sidebarOpen) {
          toggleSidebarOpen()
        }
      }
    }

    // Orientation change event listener
    window.addEventListener('orientationchange', handleOrientationChange)
    
    // Resize event listener for devices without orientation API
    const handleResize = () => {
      if ((isTablet() || isMobile()) && typeof window !== 'undefined') {
        const isLandscape = window.innerWidth > window.innerHeight
        if (isLandscape && sidebarOpen) {
          toggleSidebarOpen()
        }
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('resize', handleResize)
    }
  }, [sidebarOpen, toggleSidebarOpen])

  const toggleExpanded = (href: string) => {
    setExpandedItems(prev => 
      prev.includes(href) 
        ? prev.filter(item => item !== href)
        : [...prev, href]
    )
  }

  const isActive = (href: string, hasChildren: boolean = false) => {
    // For parent items with children, only activate if we're exactly on that path or its direct children
    if (hasChildren) {
      return location.pathname === href || location.pathname.startsWith(href + '/')
    }
    // For leaf items, check exact match or if it's a sub-path
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const renderNavItem = (item: NavItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.href)
    const active = isActive(item.href, hasChildren)

    if (hasChildren) {
      return (
        <div key={item.href} className="space-y-1">
          <div className="flex items-center">
            <div
              onClick={() => toggleExpanded(item.href)}
              className={`
                flex-1 flex items-center px-3 py-2 text-sm font-medium rounded-lg cursor-pointer
                transition-all duration-200 group
                ${active 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
                ${level > 0 ? 'ml-4' : ''}
              `}
            >
              <div className="flex items-center">
                <item.icon className={`
                  w-5 h-5 mr-3 transition-colors duration-200
                  ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'}
                 `} />
                <span className="truncate">{item.name}</span>
              </div>
            </div>
            <button
              onClick={() => toggleExpanded(item.href)}
              className={`
                p-2 rounded-lg transition-all duration-200 flex items-center justify-center
                ${active 
                  ? 'text-white hover:bg-white/20' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
              `}
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {isExpanded && (
            <div className="ml-4 space-y-1">
              {item.children?.map(child => renderNavItem(child, level + 1))}
            </div>
          )}
        </div>
      )
    }

    return (
      <NavLink
        key={item.href}
        to={item.href}
        onClick={() => {
          // Tüm cihazlarda menüyü kapat
          toggleSidebarOpen()
        }}
        className={({ isActive }) => `
          flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg
          transition-all duration-200 group
          ${isActive 
            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105' 
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:transform hover:scale-105'
          }
          ${level > 0 ? 'ml-4' : ''}
        `}
      >
        {({ isActive }) => (
          <>
            <div className="relative flex items-center">

              <item.icon
                className={`
                  w-5 h-5 mr-3 transition-colors duration-200
                  ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'}
                `}
              />
               <span className="truncate">{item.name}</span>
            </div>
            {item.badge && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    )
  }

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0 lg:translate-x-0' : '-translate-x-full lg:-translate-x-full'}
        lg:w-64 w-64
        flex flex-col h-full
      `}
    >
      {/* Mobile Header */}
      <div className="px-4 py-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 lg:hidden">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
            <span className="text-white font-bold">L</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">LGSTAKİP</span>
        </div>
        <button
          onClick={toggleSidebarOpen}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* User Info */}
      {user && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user.name}
              </p>
              <div className="flex items-center space-x-2">
                <div className="flex items-center">
                  <StarIcon className="w-3 h-3 text-yellow-400 mr-1" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Seviye {user.level}
                  </span>
                </div>
                <div className="flex items-center">
                  <FireIcon className="w-3 h-3 text-orange-400 mr-1" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {user.xp} XP
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* XP Progress Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>İlerleme</span>
              <span>{Math.round((user.xp % 1000) / 10)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(user.xp % 1000) / 10}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
        {navigationItems
          .filter(item => {
            // Yönetim menüsünü sadece admin kullanıcılara göster
            if (item.name === 'Yönetim') {
              return isAdmin;
            }
            return true;
          })
          .map(item => renderNavItem(item))}
      </nav>

      {/* Bottom Navigation */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
        {bottomNavigationItems.map(item => {
          // Bildirimler için dinamik badge ekle
          if (item.name === 'Bildirimler' && unreadCount > 0) {
            return renderNavItem({ ...item, badge: unreadCount })
          }
          return renderNavItem(item)
        })}
      </div>


    </div>
  )
}

export default Sidebar